// ==============================================================================
// REFACTORED FRONTEND SCORING FUNCTIONS TO MATCH BACKEND
// ==============================================================================

import { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";
import type { Player } from "quiz/types/player.model";

// Constants matching backend
const EXACT_MATCH_BONUS = 25;
const MAX_DISTANCE = 999;
const DISTANCE_OFFSET = 2;

/** Toggle: set false to revert to legacy 3-tier OrderedAccuracy scoring */
const USE_NEW_ORDERED_ACCURACY_SCORING = true;

interface DoubleInputMetrics {
  exactScore: 0 | 1;
  exactResult: 0 | 1;
  winnerExactValue: 0 | 1;
  resultGap: number;
  sumDifferenceGoals: number;
  absDifferenceGoals: number;
  differenceGap: number;
  goalsNumber: number;
}

interface SingleInputMetrics {
  minuteGap: number;
}

interface SortCriterion {
  predictionIndex: number;
  metricField: string;
  direction: "ASC" | "DESC";
}

interface PlayerWithScore {
  player: Player;
  score: number;
  rank?: number;
  scores?: Record<string, number>;
  metrics?: Record<string, DoubleInputMetrics | SingleInputMetrics>;
}

interface PredictionEntry {
  index: number;
  values: (number | string)[];
}

interface ResultData {
  index: number;
  values: (number | string)[];
  isDoubleInput: boolean;
}

// ==============================================================================
// HELPER FUNCTIONS (matching backend)
// ==============================================================================

/**
 * Determines field types from results (matching backend determineFieldTypes)
 */
function determineFieldTypes(pronosticV2: PronosticV2): ResultData[] {
  return pronosticV2.specificSettings.pronosticResults.map((result) => {
    const isDoubleInput = result.field2 !== null;
    return {
      index: result.pronosticIndex,
      isDoubleInput,
      values: isDoubleInput ? [result.field1, result.field2!] : [result.field1],
    };
  });
}

/**
 * Determines dynamic sort order from win condition (matching backend determineSortOrder)
 */
function determineSortOrder(
  pronosticV2: PronosticV2,
  numberOfPredictions: number,
): number[] {
  try {
    const allPronosticIndices =
      pronosticV2.commonSettings.general.moduleSettings.winCondition.content.map(
        (item) => item.pronosticIndex,
      );

    const uniqueIndices = Array.from(new Set(allPronosticIndices));

    const sortedIndices = uniqueIndices.sort((a, b) => {
      const aWinCondition =
        pronosticV2.commonSettings.general.moduleSettings.winCondition.content.find(
          (item) => item.pronosticIndex === a,
        );
      const bWinCondition =
        pronosticV2.commonSettings.general.moduleSettings.winCondition.content.find(
          (item) => item.pronosticIndex === b,
        );

      const aPriority = aWinCondition?.winConditionIndex || 0;
      const bPriority = bWinCondition?.winConditionIndex || 0;

      return aPriority - bPriority;
    });

    return sortedIndices.length > 0 ? sortedIndices : [];
  } catch {
    return [];
  }
}

// ==============================================================================
// NEW MULTI-DIMENSIONAL SCORING (OrderedAccuracy) - MATCHING BACKEND
// ==============================================================================

function computeDoubleInputMetrics(
  predicted: [number, number],
  actual: [number, number],
): DoubleInputMetrics {
  const [A, B] = predicted;
  const [Ax, Bx] = actual;

  const exactScore: 0 | 1 = A === Ax && B === Bx ? 1 : 0;
  const scoreResult = Math.sign(A - B);
  const scoreResultX = Math.sign(Ax - Bx);
  const exactResult: 0 | 1 = scoreResult === scoreResultX ? 1 : 0;
  // winnerExactValue: 1 if exact result is correct AND the winning team's score matches
  const winnerScore = scoreResult === 1 ? A : B;
  const winnerScoreX = scoreResultX === 1 ? Ax : Bx;
  const winnerExactValue: 0 | 1 = exactResult === 1 ? (winnerScore === winnerScoreX ? 1 : 0) : 0;

  // resultGap: distance between result directions
  const resultGap = Math.abs(scoreResult - scoreResultX);

  const differenceGap = Math.abs(A - B - (Ax - Bx));
  const goalsGap = Math.abs(A + B - (Ax + Bx));
  const sumDifferenceGoals = differenceGap + goalsGap;
  const absDifferenceGoals = Math.abs(differenceGap - goalsGap);

  // goalsNumber: total predicted goals (0 for draw predictions)
  const goalsNumber = A === B ? 0 : A + B;

  return {
    exactScore,
    exactResult,
    winnerExactValue,
    resultGap,
    sumDifferenceGoals,
    absDifferenceGoals,
    differenceGap,
    goalsNumber,
  };
}

function computeSingleInputMetrics(
  predicted: number,
  actual: number,
): SingleInputMetrics {
  return { minuteGap: Math.abs(predicted - actual) };
}

function calculatePlayerMetrics(
  prediction: PredictionEntry[] | undefined,
  results: ResultData[],
  numberOfPredictions: number,
): {
  metrics: Record<string, DoubleInputMetrics | SingleInputMetrics>;
  legacyScores: Record<string, number>;
} {
  const metrics: Record<string, DoubleInputMetrics | SingleInputMetrics> = {};
  const legacyScores: Record<string, number> = {};
  for (let i = 1; i <= numberOfPredictions; i++) {
    legacyScores[`score${i}`] = 0;
  }

  if (!prediction) {
    return { metrics, legacyScores };
  }

  for (const predictionEntry of prediction) {
    const resultData = results.find((r) => r.index === predictionEntry.index);
    if (!resultData) continue;

    const key = `pred${predictionEntry.index}`;
    const scoreKey = `score${predictionEntry.index}`;

    if (resultData.isDoubleInput && resultData.values.length === 2) {
      const predicted: [number, number] = [
        typeof predictionEntry.values[0] === "number"
          ? predictionEntry.values[0]
          : 0,
        typeof predictionEntry.values[1] === "number"
          ? predictionEntry.values[1]
          : 0,
      ];
      const actual: [number, number] = [
        typeof resultData.values[0] === "number" ? resultData.values[0] : 0,
        typeof resultData.values[1] === "number" ? resultData.values[1] : 0,
      ];

      const m = computeDoubleInputMetrics(predicted, actual);
      metrics[key] = m;

      if (m.exactScore === 1) legacyScores[scoreKey] = 2;
      else if (m.exactResult === 1) legacyScores[scoreKey] = 1;
      else legacyScores[scoreKey] = 0;
    } else if (!resultData.isDoubleInput && resultData.values.length === 1) {
      const predictedVal =
        typeof predictionEntry.values[0] === "number"
          ? predictionEntry.values[0]
          : 0;
      const actualVal =
        typeof resultData.values[0] === "number" ? resultData.values[0] : 0;

      const m = computeSingleInputMetrics(predictedVal, actualVal);
      metrics[key] = m;

      legacyScores[scoreKey] =
        m.minuteGap === 0
          ? EXACT_MATCH_BONUS
          : EXACT_MATCH_BONUS *
            (1 - (m.minuteGap + 1) / (MAX_DISTANCE + DISTANCE_OFFSET));
    }
  }

  return { metrics, legacyScores };
}

function buildSortCriteria(
  pronosticV2: PronosticV2,
  results: ResultData[],
): SortCriterion[] {
  const numberOfPredictions = results.length;
  const sortOrder = determineSortOrder(pronosticV2, numberOfPredictions);
  const criteria: SortCriterion[] = [];

  for (const predIndex of sortOrder) {
    const resultData = results.find((r) => r.index === predIndex);
    if (!resultData) continue;

    if (resultData.isDoubleInput && resultData.values.length === 2) {
      criteria.push(
        {
          predictionIndex: predIndex,
          metricField: "exactScore",
          direction: "DESC",
        },
        {
          predictionIndex: predIndex,
          metricField: "exactResult",
          direction: "DESC",
        },
        {
          predictionIndex: predIndex,
          metricField: "winnerExactValue",
          direction: "DESC",
        },
        {
          predictionIndex: predIndex,
          metricField: "resultGap",
          direction: "ASC",
        },
        {
          predictionIndex: predIndex,
          metricField: "sumDifferenceGoals",
          direction: "ASC",
        },
        {
          predictionIndex: predIndex,
          metricField: "absDifferenceGoals",
          direction: "ASC",
        },
        {
          predictionIndex: predIndex,
          metricField: "differenceGap",
          direction: "ASC",
        },
        {
          predictionIndex: predIndex,
          metricField: "goalsNumber",
          direction: "DESC",
        },
      );
    } else if (!resultData.isDoubleInput) {
      criteria.push({
        predictionIndex: predIndex,
        metricField: "minuteGap",
        direction: "ASC",
      });
    }
  }

  return criteria;
}

function comparePlayerMetrics(
  playerA: PlayerWithScore,
  playerB: PlayerWithScore,
  sortCriteria: SortCriterion[],
): number {
  if (!playerA.metrics || !playerB.metrics) return 0;

  for (const criterion of sortCriteria) {
    const key = `pred${criterion.predictionIndex}`;
    const metricsA = playerA.metrics[key];
    const metricsB = playerB.metrics[key];

    if (!metricsA || !metricsB) continue;

    const valA = (metricsA as any)[criterion.metricField] ?? 0;
    const valB = (metricsB as any)[criterion.metricField] ?? 0;

    if (valA !== valB) {
      return criterion.direction === "ASC" ? valA - valB : valB - valA;
    }
  }
  return 0;
}

// ==============================================================================
// CALCULATE PLAYER SCORES (OrderedAccuracy) - LEGACY
// ==============================================================================

/**
 * Calculates player scores for OrderedAccuracy win condition (legacy 3-tier)
 */
function calculatePlayerScores(
  prediction: PredictionEntry[] | undefined,
  results: ResultData[],
  numberOfPredictions: number,
  pronosticV2: PronosticV2,
): Record<string, number> {
  // Initialize all scores to 0
  const scores: Record<string, number> = {};
  for (let i = 1; i <= numberOfPredictions; i++) {
    scores[`score${i}`] = 0;
  }

  // If no prediction data, return zero scores
  if (!prediction) {
    return scores;
  }

  // Process each prediction entry
  for (const predictionEntry of prediction) {
    const resultData = results.find(
      (result) => result.index === predictionEntry.index,
    );
    if (!resultData) {
      continue;
    }

    const scoreKey = `score${predictionEntry.index}`;

    // Handle double input predictions (exact match)
    if (resultData.isDoubleInput && resultData.values.length === 2) {
      const isExactMatch =
        predictionEntry.values.length === 2 &&
        predictionEntry.values[0] === resultData.values[0] &&
        predictionEntry.values[1] === resultData.values[1];

      const isNumberArrayOfLength2 = (
        values: unknown,
      ): values is [number, number] =>
        Array.isArray(values) &&
        values.length === 2 &&
        values.every((v) => typeof v === "number" && Number.isFinite(v));

      const getOutcome = ([left, right]: [number, number]) => {
        if (left > right) return "LEFT_WIN";
        if (left < right) return "RIGHT_WIN";
        return "DRAW";
      };

      const canEvaluateMatch =
        isNumberArrayOfLength2(predictionEntry.values) &&
        isNumberArrayOfLength2(resultData.values);

      let isGoodMatch = false;

      if (canEvaluateMatch) {
        // Type assertion after validation
        const predictionValues = predictionEntry.values as [number, number];
        const resultValues = resultData.values as [number, number];

        isGoodMatch = getOutcome(predictionValues) === getOutcome(resultValues);
      }

      // Scoring logic
      if (isExactMatch) {
        scores[scoreKey] = 2;
      } else if (canEvaluateMatch && isGoodMatch) {
        scores[scoreKey] = 1;
      } else {
        scores[scoreKey] = 0;
      }
    }
    // Handle single input predictions
    else if (!resultData.isDoubleInput && resultData.values.length === 1) {
      const masterWinCondition =
        pronosticV2.commonSettings.general.moduleSettings.winCondition;
      const isWinConditionTypeClosest =
        masterWinCondition.content[predictionEntry.index - 1]?.type ===
        "closest";

      if (isWinConditionTypeClosest) {
        if (typeof resultData.values[0] === "number") {
          const first = predictionEntry.values[0];
          const predictedValue = typeof first === "number" ? first : 0;
          const actualValue = resultData.values[0];
          const distance = Math.abs(predictedValue - actualValue);

          if (distance === 0) {
            // Exact match - full bonus
            scores[scoreKey] += EXACT_MATCH_BONUS;
          } else {
            // Distance-based scoring
            const distanceFactor =
              1 - (distance + 1) / (MAX_DISTANCE + DISTANCE_OFFSET);
            scores[scoreKey] += EXACT_MATCH_BONUS * distanceFactor;
          }
        }
      } else {
        // Exact match mode
        const predictedValue = predictionEntry.values[0] || 0;
        const actualValue = resultData.values[0];

        if (predictedValue === actualValue) {
          scores[scoreKey] += 1;
        } else {
          scores[scoreKey] += 0;
        }
      }
    }
  }

  return scores;
}

// ==============================================================================
// CALCULATE PLAYER POINTS (PointsCalculation) - MATCHING BACKEND
// ==============================================================================

/**
 * Calculates player points for PointsCalculation win condition
 * This now matches the backend implementation exactly
 */
function calculatePlayerPoints(
  prediction: PredictionEntry[] | undefined,
  results: ResultData[],
  numberOfPredictions: number,
  pronosticV2: PronosticV2,
): Record<string, number> {
  const masterWinCondition =
    pronosticV2.commonSettings.general.moduleSettings.winCondition;

  // Initialize all scores to 0
  const scores: Record<string, number> = {};
  for (let i = 1; i <= numberOfPredictions; i++) {
    scores[`score${i}`] = 0;
  }

  // If no prediction data, return zero scores
  if (!prediction) {
    return scores;
  }

  // Process each prediction entry
  for (let i = 0; i < prediction.length; i++) {
    const predictionEntry = prediction[i];
    const resultData = results.find(
      (result) => result.index === predictionEntry.index,
    );
    if (!resultData) {
      continue;
    }

    const scoreKey = `score${predictionEntry.index}`;

    const elementType =
      pronosticV2.commonSettings.general.moduleSettings.pronostics[i]?.type;
    const pointsCalculationConfigType =
      masterWinCondition.pointsCalc?.[i]?.type;

    if (elementType === "input" || elementType === "") {
      // Handle double input predictions
      if (resultData.isDoubleInput && resultData.values.length === 2) {
        let points = 0;

        // Helper to convert to number
        const num = (v: number | string) => (typeof v === "number" ? v : 0);

        // Check for rough match (correct result direction)
        if (
          (resultData.values[0] < resultData.values[1] &&
            predictionEntry.values[0] < predictionEntry.values[1]) ||
          (resultData.values[0] > resultData.values[1] &&
            predictionEntry.values[0] > predictionEntry.values[1]) ||
          (resultData.values[0] === resultData.values[1] &&
            predictionEntry.values[0] === predictionEntry.values[1])
        ) {
          points +=
            masterWinCondition.pointsCalc?.[predictionEntry.index - 1]
              ?.fourWeights?.correctResult || 4;
        }

        const aMatch =
          predictionEntry.values.length === 2 &&
          predictionEntry.values[0] === resultData.values[0];
        const bMatch =
          predictionEntry.values.length === 2 &&
          predictionEntry.values[1] === resultData.values[1];

        const isUserDifferenceSameAsResultDifference =
          num(predictionEntry.values[1]) - num(predictionEntry.values[0]) ===
          num(resultData.values[1]) - num(resultData.values[0]);

        if (aMatch) {
          points +=
            masterWinCondition.pointsCalc?.[predictionEntry.index - 1]
              ?.fourWeights?.aExact || 2;
        }
        if (bMatch) {
          points +=
            masterWinCondition.pointsCalc?.[predictionEntry.index - 1]
              ?.fourWeights?.bExact || 2;
        }
        if (isUserDifferenceSameAsResultDifference) {
          points +=
            masterWinCondition.pointsCalc?.[predictionEntry.index - 1]
              ?.fourWeights?.exactMatch || 2;
        }

        scores[scoreKey] = points;
      }
      // Handle single input predictions
      else if (!resultData.isDoubleInput && resultData.values.length === 1) {
        if (pointsCalculationConfigType === "DistanceCalculation") {
          const predictedValue = predictionEntry.values[0] || 0;
          const actualValue = resultData.values[0] || 0;

          const num = (v: number | string) => (typeof v === "number" ? v : 0);

          const definedPoints =
            masterWinCondition.pointsCalc?.[predictionEntry.index - 1]
              ?.distanceCalc?.weightsDefinedPoints?.definiedPoints || 10;

          let distanceScore = 0;
          const distance = Math.abs(num(actualValue) - num(predictedValue));
          if (distance < definedPoints) {
            distanceScore = definedPoints - distance;
          } else {
            distanceScore = 0;
          }

          scores[scoreKey] += distanceScore;
        }
      }
    } else if (elementType === "slideshow") {
      // Only exact match for slideshow
      const predictedValue = predictionEntry.values[0] || 0;
      const actualValue = resultData.values[0] || 0;

      const areTheValuesEqual = predictedValue === actualValue;

      if (areTheValuesEqual) {
        scores[scoreKey] +=
          masterWinCondition.pointsCalc?.[predictionEntry.index - 1]
            ?.exactMatchCalc?.exactMatchPoints || 5;
      }
    }
  }

  return scores;
}

// ==============================================================================
// TOTAL SCORE CALCULATION
// ==============================================================================

function calculateTotalScore(
  scores: Record<string, number>,
  numberOfPredictions: number,
): number {
  let total = 0;
  for (let i = 1; i <= numberOfPredictions; i++) {
    total += scores[`score${i}`] || 0;
  }
  return total;
}

// ==============================================================================
// PLAYER COMPARISON FUNCTIONS
// ==============================================================================

/**
 * Compares players by individual scores for OrderedAccuracy
 */
function comparePlayerScores(
  playerA: PlayerWithScore,
  playerB: PlayerWithScore,
  sortOrder: number[],
  winConditionData: string[],
): number {
  if (!playerA.scores || !playerB.scores) return 0;

  for (const scoreNum of sortOrder) {
    const scoreKey = `score${scoreNum}`;
    const scoreA = playerA.scores[scoreKey] || 0;
    const scoreB = playerB.scores[scoreKey] || 0;

    if (scoreA !== scoreB) {
      const condition = winConditionData[scoreNum - 1];
      if (condition === "exact" || condition === "closest") {
        return scoreB - scoreA; // Higher score wins
      }
    }
  }
  return 0;
}

/**
 * Compares players by total score for PointsCalculation
 */
function compareByTotalScore(
  playerA: PlayerWithScore,
  playerB: PlayerWithScore,
): number {
  return playerB.score - playerA.score; // Descending order
}

// ==============================================================================
// RANK ASSIGNMENT FUNCTIONS
// ==============================================================================

/**
 * Assigns ranks based on individual scores (OrderedAccuracy)
 */
function assignRanks(
  sortedPlayers: PlayerWithScore[],
  numberOfPredictions: number,
  sortCriteria?: SortCriterion[],
): PlayerWithScore[] {
  if (sortedPlayers.length === 0) return sortedPlayers;

  sortedPlayers[0].rank = 1;

  for (let i = 1; i < sortedPlayers.length; i++) {
    const previousPlayer = sortedPlayers[i - 1];
    const currentPlayer = sortedPlayers[i];

    let isTied: boolean;
    if (sortCriteria) {
      // New OrderedAccuracy: use granular metrics comparison
      isTied = comparePlayerMetrics(previousPlayer, currentPlayer, sortCriteria) === 0;
    } else {
      // Legacy: compare legacy scores
      isTied = Array.from(
        { length: numberOfPredictions },
        (_, index) => `score${index + 1}`,
      ).every(
        (scoreKey) =>
          currentPlayer.scores?.[scoreKey] === previousPlayer.scores?.[scoreKey],
      );
    }

    currentPlayer.rank = isTied ? previousPlayer.rank : i + 1;
  }

  return sortedPlayers;
}

/**
 * Assigns ranks based on total score (PointsCalculation)
 */
function assignRanksByTotalScore(
  sortedPlayers: PlayerWithScore[],
): PlayerWithScore[] {
  if (sortedPlayers.length === 0) return sortedPlayers;

  sortedPlayers[0].rank = 1;

  for (let i = 1; i < sortedPlayers.length; i++) {
    const previousPlayer = sortedPlayers[i - 1];
    const currentPlayer = sortedPlayers[i];

    currentPlayer.rank =
      currentPlayer.score === previousPlayer.score
        ? previousPlayer.rank
        : i + 1;
  }

  return sortedPlayers;
}

// ==============================================================================
// PREDICTION STRING FORMATTING
// ==============================================================================

/**
 * Formats prediction values into a string representation (matching backend)
 */
function formatPredictionString(
  prediction: PredictionEntry[] | undefined,
  fieldTypes: Array<{ index: number; isDoubleInput: boolean }>,
): string {
  if (!prediction) {
    return "";
  }

  // Sort predictions by index to ensure consistent order
  const sortedPredictions = [...prediction].sort((a, b) => a.index - b.index);

  const formattedPredictions = sortedPredictions.map((pred) => {
    const fieldType = fieldTypes.find((ft) => ft.index === pred.index);
    if (!fieldType) {
      return pred.values.join("-"); // Fallback
    }

    if (fieldType.isDoubleInput && pred.values.length === 2) {
      return `${pred.values[0]}-${pred.values[1]}`;
    } else {
      return `${pred.values[0]}`;
    }
  });

  return formattedPredictions.join(", ");
}

// ==============================================================================
// MAIN EXPORTED FUNCTIONS
// ==============================================================================

/**
 * Calculates scores for all players based on win condition type
 * This replaces both calculatePlayerScores and calculatePlayerPoints in the original code
 */
export function calculatePlayersWithScores(
  players: Player[],
  pronosticV2: PronosticV2,
): PlayerWithScore[] {
  const numberOfPredictions =
    pronosticV2.specificSettings.pronosticResults.length;
  const winConditionType =
    pronosticV2.commonSettings.general.moduleSettings.winCondition.type;
  const results = determineFieldTypes(pronosticV2);

  return players.map((player) => {
    if (!player.pronosticInfo?.prediction) {
      return { player, score: 0, scores: {} };
    }

    let playerScores: Record<string, number>;
    let playerMetrics:
      | Record<string, DoubleInputMetrics | SingleInputMetrics>
      | undefined;

    if (winConditionType === "OrderedAccuracy") {
      if (USE_NEW_ORDERED_ACCURACY_SCORING) {
        const result = calculatePlayerMetrics(
          player.pronosticInfo.prediction,
          results,
          numberOfPredictions,
        );
        playerScores = result.legacyScores;
        playerMetrics = result.metrics;
      } else {
        playerScores = calculatePlayerScores(
          player.pronosticInfo.prediction,
          results,
          numberOfPredictions,
          pronosticV2,
        );
      }
    } else if (winConditionType === "PointsCalculation") {
      playerScores = calculatePlayerPoints(
        player.pronosticInfo.prediction,
        results,
        numberOfPredictions,
        pronosticV2,
      );
    } else {
      playerScores = {};
    }

    const totalScore = calculateTotalScore(playerScores, numberOfPredictions);

    return {
      player,
      score: totalScore,
      scores: playerScores,
      ...(playerMetrics ? { metrics: playerMetrics } : {}),
    };
  });
}

/**
 * Ranks players and returns them sorted with ranks assigned
 */
export function rankPlayersAndFormatWinners(
  players: Player[],
  pronosticV2: PronosticV2,
  subPlaceIndex: string,
  subPlaceTitle?: string,
) {
  const numberOfPredictions =
    pronosticV2.specificSettings.pronosticResults.length;
  const winConditionType =
    pronosticV2.commonSettings.general.moduleSettings.winCondition.type;

  // Get field types for prediction string formatting
  const results = determineFieldTypes(pronosticV2);
  const fieldTypes = results.map((result) => ({
    index: result.index,
    isDoubleInput: result.isDoubleInput,
  }));

  // Separate players with and without predictions
  const playersWithPredictions = players.filter((p) => p.pronosticInfo);
  const playersWithoutPredictions = players.filter((p) => !p.pronosticInfo);

  // Calculate scores
  let playersWithScores = calculatePlayersWithScores(
    playersWithPredictions,
    pronosticV2,
  );

  // Sort and rank based on win condition type
  let rankedPlayers: PlayerWithScore[];

  if (winConditionType === "PointsCalculation") {
    // Sort by total score
    playersWithScores.sort(compareByTotalScore);
    rankedPlayers = assignRanksByTotalScore(playersWithScores);
  } else if (
    winConditionType === "OrderedAccuracy" &&
    USE_NEW_ORDERED_ACCURACY_SCORING
  ) {
    // New multi-criteria OrderedAccuracy sorting
    const sortCriteria = buildSortCriteria(pronosticV2, results);
    playersWithScores.sort((a, b) =>
      comparePlayerMetrics(a, b, sortCriteria),
    );
    rankedPlayers = assignRanks(playersWithScores, numberOfPredictions, sortCriteria);
  } else {
    // Legacy OrderedAccuracy - sort by individual scores
    const sortOrder = determineSortOrder(pronosticV2, numberOfPredictions);
    const winConditionData =
      pronosticV2.commonSettings.general.moduleSettings.winCondition.content.map(
        (item) => item.type,
      );

    playersWithScores.sort((a, b) =>
      comparePlayerScores(a, b, sortOrder, winConditionData),
    );
    rankedPlayers = assignRanks(playersWithScores, numberOfPredictions);
  }

  // Assign rank to players without predictions
  const highestRank =
    rankedPlayers.length > 0
      ? Math.max(...rankedPlayers.map((p) => p.rank || 0))
      : 0;

  const rankedPlayersWithoutPredictions = playersWithoutPredictions.map(
    (player) => ({
      player,
      score: 0,
      rank: highestRank + 1,
      scores: {},
    }),
  );

  const allRankedPlayers = [
    ...rankedPlayers,
    ...rankedPlayersWithoutPredictions,
  ];

  // Format as PronosticV2Winner with predictionString
  return {
    subPlaceIndex,
    subPlaceTitle,
    winner: allRankedPlayers.map(({ player, rank, score }) => ({
      id: player.id,
      partitionId: player.partitionId,
      rank: rank || 0,
      name: `${player.firstName} ${player.lastName}`,
      phoneNumber: player.phoneNumber,
      mail: player.email,
      totalScore: score,
      room: player.room,
      enterprise: player.enterprise,
      predictionString: formatPredictionString(
        player.pronosticInfo?.prediction,
        fieldTypes,
      ),
    })),
  };
}
